import { useState } from "react";
import { api, ApiError } from "../api";
import type { Row } from "../types";
import { IconClose, IconCheck, IconFile } from "../icons";

interface Props {
  atividade: Row;
  onClose: () => void;
  onEnviado: () => void;
}

/**
 * Ensaios que o Laboratório sabe fazer. As chaves são as MESMAS de lá
 * (SECOES_ANALISE) — é por elas que a tela de configuração do laudo decide
 * quais abas mostrar. Trocar o texto aqui é seguro; trocar a chave, não.
 */
const ENSAIOS = [
  { chave: "fisicoQuimica", rotulo: "FQ · Físico-Química" },
  { chave: "agdGases", rotulo: "CR · Cromatografia (gases)" },
  { chave: "pcb", rotulo: "PCB · Bifenilas policloradas" },
  { chave: "furfuraldeido", rotulo: "2-FALL · Furfuraldeído" },
];

/**
 * Envio do material da OS ao Laboratório.
 *
 * O que sai daqui é o AVISO de que a caixa vai chegar: cria a remessa lá (status
 * "Aguardando", pronta para bipagem) e, quando há ensaio, abre a análise. O
 * número da OS não é editável de propósito — ele vem da atividade, porque é a
 * chave que amarra remessa, análise e, mais tarde, o laudo no Relatórios.
 */
export default function EnviarLaboratorioModal({ atividade, onClose, onEnviado }: Props) {
  const [quantidade, setQuantidade] = useState("1");
  const [equipamento, setEquipamento] = useState("");
  const [criarAnalise, setCriarAnalise] = useState(true);
  const [ensaios, setEnsaios] = useState<string[]>(["fisicoQuimica"]);
  const [prazoColeta, setPrazoColeta] = useState("");
  const [prazoRetorno, setPrazoRetorno] = useState("");
  const [observacao, setObservacao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const semOs = !String(atividade.codigo_os ?? "").trim();

  function alternarEnsaio(chave: string) {
    setEnsaios((atuais) =>
      atuais.includes(chave) ? atuais.filter((c) => c !== chave) : [...atuais, chave],
    );
  }

  async function enviar() {
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.separacao.enviarLaboratorio(atividade.id, {
        quantidade_prevista: Number(quantidade),
        equipamento: equipamento.trim(),
        observacao: observacao.trim(),
        criar_analise: criarAnalise,
        prazo_coleta: prazoColeta,
        prazo_retorno: prazoRetorno,
        analises: criarAnalise ? ensaios : [],
      });
      const reaproveitou = r.remessaReaproveitada
        ? " (a remessa desta OS já existia no Laboratório e foi atualizada — não virou uma segunda caixa)"
        : "";
      const comAnalise = r.analiseId
        ? ` A análise nº ${r.analiseId} está na fila de ensaio.`
        : "";
      setOk(
        `Material da OS ${r.osNumber} registrado no Laboratório${reaproveitou}.${comAnalise} ` +
          "O laboratório já pode conferir a caixa item a item.",
      );
      onEnviado();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Falha ao enviar ao Laboratório.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconFile size={18} /> Enviar para o laboratório
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="sep-resumo">
            <strong>
              {atividade.codigo_os ? `OS ${atividade.codigo_os}` : "Atividade sem OS"}
              {atividade.cliente ? ` · ${atividade.cliente}` : ""}
            </strong>
            <span>{String(atividade.nome ?? "—")}</span>
          </div>

          {semOs ? (
            <div className="form-error">
              Esta atividade não tem número de OS. O Laboratório organiza a remessa POR OS —
              preencha a OS na aba Atividades antes de enviar o material.
            </div>
          ) : (
            <>
              <div className="form-grid">
                <label className="field">
                  <span>Frascos / seringas *</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quantidade}
                    onChange={(e) => setQuantidade(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Equipamento / TAG</span>
                  <input
                    type="text"
                    value={equipamento}
                    placeholder="Ex.: TR-01 · 500 kVA"
                    onChange={(e) => setEquipamento(e.target.value)}
                  />
                </label>
              </div>

              <p className="sep-nota">
                A quantidade é a régua da conferência: no Laboratório, cada frasco é bipado e a
                diferença para este número é o que decide se a caixa pode ser fechada.
              </p>

              <label className="sep-check">
                <input
                  type="checkbox"
                  checked={criarAnalise}
                  onChange={(e) => setCriarAnalise(e.target.checked)}
                />
                <span>
                  Abrir também a <strong>análise</strong> (ensaio de óleo) — desmarque se o material
                  vai só para conferência/devolução, sem laudo.
                </span>
              </label>

              {criarAnalise && (
                <>
                  <div className="sep-ensaios">
                    {ENSAIOS.map((e) => (
                      <label key={e.chave} className="sep-check">
                        <input
                          type="checkbox"
                          checked={ensaios.includes(e.chave)}
                          onChange={() => alternarEnsaio(e.chave)}
                        />
                        <span>{e.rotulo}</span>
                      </label>
                    ))}
                  </div>
                  <div className="form-grid">
                    <label className="field">
                      <span>Prazo de coleta</span>
                      <input
                        type="date"
                        value={prazoColeta}
                        onChange={(e) => setPrazoColeta(e.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span>Prazo de retorno</span>
                      <input
                        type="date"
                        value={prazoRetorno}
                        onChange={(e) => setPrazoRetorno(e.target.value)}
                      />
                    </label>
                  </div>
                </>
              )}

              <div className="form-grid">
                <label className="field field-wide">
                  <span>Observação para o laboratório (opcional)</span>
                  <textarea
                    rows={2}
                    value={observacao}
                    placeholder="Ex.: amostra coletada com o transformador energizado..."
                    onChange={(e) => setObservacao(e.target.value)}
                  />
                </label>
              </div>
            </>
          )}

          {erro && <div className="form-error">{erro}</div>}
          {ok && (
            <div className="form-ok">
              <IconCheck size={16} /> {ok}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {ok ? "Fechar" : "Cancelar"}
          </button>
          {!ok && !semOs && (
            <button type="button" className="btn-primary" onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando..." : "Enviar ao laboratório"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
